import * as React from 'react';
import { AnyObject } from 'final-form';
import LeaseClause from 'src/models/LeaseClause';
import { Page, Text, Document, StyleSheet, View } from '@react-pdf/renderer';
import PropertyUtil from 'src/util/PropertyUtil';
import moment from 'moment';
import MathUtil from 'src/util/MathUtil';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import SignatureBlock from 'src/models/leasing/SignatureBlock';
import LeaseClauseType from 'src/models/LeaseClauseType';
import LocationModel from 'src/models/leasing/LocationModel';
import ContractAddendumClause from '../redesign/ContractGenerationModels/ContractAddendumClause';
import Lease from 'src/models/leasehold/Lease';

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
        paddingTop: 60,
        paddingBottom: 60,
        paddingLeft: 60,
        paddingRight: 60,
        fontFamily: 'Times-Roman',
    },
    pageNumbers: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#8e9094',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    bold: {
        fontFamily: 'Times-Bold',
    },
    fontNormal: {
        fontFamily: 'Times-Roman',
        fontSize: '11pt',
        lineHeight: 1.25,
    },
    font9pt: {
        fontFamily: 'Times-Roman',
        fontSize: '9pt',
        lineHeight: 1.25,
    },
    noticeHeadingSize: {
        fontSize: '12pt',
    },
    mt0: {
        marginTop: 0,
    },
    mt1: {
        marginTop: 10,
    },
    mt2: {
        marginTop: 20,
    },
    mt3: {
        marginTop: 30,
    },
    mt4: {
        marginTop: 40,
    },
    center: {
        textAlign: 'center',
    },
    indent: {
        textIndent: '.2in',
    },
    indent2: {
        textIndent: '.3in',
    },
    indent5: {
        textIndent: '2.35in',
    },
    text: {
        textAlign: 'justify',
    },
    sbTitle: {
        textAlign: 'center',
        marginBottom: 10,
    },
    underlined: {
        textDecoration: 'underline',
    },
    italic: {
        fontFamily: 'Times-Italic'
    },
    mailingInfo: {
        fontSize: 10,
        fontFamily: 'Times-Roman',
    },
    acknowledgementHeading: {
        textAlign: 'center',
        fontSize: '10pt',
        fontWeight: 'bold',
    },
    exhibitHeading: {
        textAlign: 'center',
        fontSize: '12pt',
        fontWeight: 'bold',
    },
    leaseHeading: {
        fontSize: 20,
        textAlign: 'center',
        fontFamily: 'Times-Bold',
    },
    doubleLines: {
        borderTop: '1px solid black',
        borderBottom: '1px solid black',
    },
    h2: {
        fontSize: 14,
    },
    firstLvl: {
        marginLeft: 20,
    },
    secondLvl: {
        marginLeft: 54,
    },
    thirdLvl: {
        marginLeft: 70,
    },
    fourthLvl: {
        marginLeft: 86,
    },
});

export interface IFormonteraLeaseStateProps {
    contractData: Lease;
    leaseClauses?: ContractAddendumClause[];
    leaseClauseTypes?: LeaseClauseType[];
    location?: LocationModel;
}

export default class FormonteraLease extends React.Component<IFormonteraLeaseStateProps> {
    private additionalProvisionNumber = 0;
    private royaltyFractionWords = '';
    private includedTracts = this.props?.contractData?.tracts?.filter(t => t.includeTract);

    displayOptionToExtend = () => {
        if (this.props?.contractData?.leasehold?.renewalOption === true) {
            this.additionalProvisionNumber++;
            return (
                <Text style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                    {this.additionalProvisionNumber}
                    {') '}For the same consideration recited in this lease, Lessor hereby grants Lessee the option to extend the primary term of this
                    lease for an additional
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                    {' '}from the expiration of the original primary term hereof as to all or any
                    portion of the acreage then held hereunder which would expire unless so extended. This option may be exercised by Lessee, or its
                    successors and assigns, at any time during the primary term hereof by tendering to Lessor the sum of{' '}
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionBonusAmount + '', 10), ' '),
                            'OPTION AMOUNT'
                        )}{' '}dollars{' '}
                        (${parseInt(this.props.contractData?.leasehold?.optionBonusAmount + '', 10)})
                    </Text>
                    {' '}per net mineral acre for each acre so extended, which
                    payment shall cover the
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                    {' '}of the extended term and Lessor acknowledges that there will be no rental
                    payments due for or during the extended term. Should the Primary Term be extended as herein provided, it shall be considered that
                    this lease originally provided for a Primary Term of
                    {this.displayTotalPrimaryTerm()}
                    . Should this option be exercised as herein provided, it is agreed that Lessee
                    may execute and file of record an appropriate recordable instrument evidencing the exercise of this option.
                </Text>
            );
        } else {
            return <></>;
        }
    };

    additionalProvisions = () => {
        let renderClauses: ContractAddendumClause[] = [];
        this.props.contractData?.contractClauses?.forEach((llc: AnyObject, index: number) => {
            let clause: ContractAddendumClause = !!this.props?.leaseClauses
                ? this.props.leaseClauses?.find((lc) => lc.id === llc.contractAddendumClauseId)
                : undefined;

            if (!!clause) {
                renderClauses.push(clause);
            }
        });

        return renderClauses?.map((rc: LeaseClause, index: number) => {
            this.additionalProvisionNumber++;
            return (
                <Text key={index} style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                    {this.additionalProvisionNumber}) {`${rc.clauseText}`}
                </Text>
            );
        });
    };

    adhocProvisions = () => {
        const adHocClauses = this?.props?.contractData?.adHocClauses;
        if (adHocClauses?.length > 0) {
            return this.props?.contractData?.adHocClauses?.map((lc: LeaseClause, index: number) => {
                let type: string = !!this.props?.leaseClauseTypes
                    ? this.props?.leaseClauseTypes?.find((lct) => lct.id === lc.leaseClauseTypeId)?.name
                    : '';

                this.additionalProvisionNumber++;
                return (
                    <Text key={index} style={[{ fontSize: '10pt' }, styles.mt2, styles.text]}>
                        {this.additionalProvisionNumber}) {`${type}: ${lc.clauseText}`}
                    </Text>
                );
            });
        }
        return <div />;
    };

    displaySignatureBlocks = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement) => {
                return (
                    la.signatureBlocks.length > 0 &&
                    la.signatureBlocks.map((sb: SignatureBlock, index: number) => {
                        return (
                            <View key={index} wrap={false} style={{ marginRight: 5 }}>
                                {!!sb.title && sb?.title.length > 0 && (
                                    <Text style={[{ fontSize: '10pt' }, styles.mt3, styles.text, styles.bold]}>{sb?.title}</Text>
                                )}
                                <Text style={[{ fontSize: '10pt' }, styles.mt4, styles.text, styles.bold]}>
                                    ____________________________________
                                </Text>
                                <Text style={[{ fontSize: '10pt' }, styles.text, styles.bold]}>{sb.ownerName}</Text>
                            </View>
                        );
                    })
                );
            })
            : [];
    };

    displayAcknowledgements = () => {
        return this.props?.contractData
            ? this.props?.contractData?.acknowledgements?.map((la: Acknowledgement, index: number) => {
                return (
                    <View key={index} wrap={false}>
                        <Text style={[{ fontSize: '10pt' }, styles.mt3, styles.text]}>{la.text}</Text>
                    </View>
                );
            })
            : [];
    };

    termValDisplay = (mode: string): string => {
        if (mode === 'normal') {
            let primaryTerm: number = !!this.props?.contractData ? parseInt(this.props?.contractData?.leasehold?.primaryTerm + '', 10) : undefined;
            let primaryTermUnits: string = !!this.props?.contractData ? this.props?.contractData?.leasehold?.primaryTermUnits : undefined;

            if (!primaryTerm && !primaryTermUnits) {
                return 'zero (0) years';
            }

            let primaryTermString = MathUtil.numberToEnglish(primaryTerm, ' ');
            primaryTermString = primaryTermString.trimLeft();

            return `${primaryTermString} (${primaryTerm}) ${primaryTermUnits}`;
        }

        if (mode === 'option') {
            let optionTerm: number = !!this.props?.contractData ? parseInt(this.props?.contractData?.leasehold?.optionTerm + '', 10) : undefined;
            let optionTermUnits: string = !!this.props?.contractData ? this.props?.contractData?.leasehold?.optionTermUnits : undefined;
            if (!optionTerm && !optionTermUnits) {
                return 'zero (0) years';
            }

            let optionTermString = MathUtil.numberToEnglish(optionTerm, ' ');
            optionTermString = optionTermString.trimLeft();

            return `${optionTermString} (${optionTerm}) ${optionTermUnits}`;
        }

        return '';
    };

    displayRoyaltyFraction = () => {
        let royaltyFraction = PropertyUtil.SafeGet(() => MathUtil.decimalToFraction(this.props.contractData?.leasehold?.royalty), 'Royalty');
        let splitRoyaltyFraction = royaltyFraction.split('/');

        let numerator = splitRoyaltyFraction[0];
        let denominator = splitRoyaltyFraction[1];

        let royaltyToEnglish = MathUtil.fractionToEnglish(numerator, denominator);
        console.log(`Fraction To Words (${this.props.contractData?.leasehold?.royalty})  => `, royaltyToEnglish);

        // Set variable so you only have to Convert once
        this.royaltyFractionWords = `${royaltyToEnglish}, or ${MathUtil.round(this.props.contractData?.leasehold?.royalty * 100, 4)}%`;
        return this.royaltyFractionWords;
    }

    displayTownshipInfo = () => {
        let townshipInfo = '';

        if (this.includedTracts?.length >= 1) {
            this.includedTracts.forEach(tract => {
                townshipInfo += `[Section ${tract.sectionNumber}, Township ${tract.township}, Range ${tract.range}],`;
            });
        }

        return townshipInfo;
    };

    displayTotalPrimaryTerm = () => {
        let primeTerm = PropertyUtil.SafeGet(() => this.props.contractData.leasehold?.primaryTerm, 'PRIMARY TERM');
        let primeUnit = this.props.contractData?.leasehold?.primaryTermUnits;

        let optionTerm = PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM');
        let optionUnit = this.props.contractData?.leasehold?.optionTermUnits;

        if (!isNaN(primeTerm) && !isNaN(optionTerm)) {
            if (optionUnit !== primeUnit) {
                return (
                    <Text style={[styles.bold, { fontSize: '9pt' }]}>
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(primeTerm, 10), ' '),
                            'PRIMARY TERM WORD'
                        )}{' '}
                        ({primeTerm}){' '}
                        {this.props.contractData?.leasehold?.primaryTermUnits}
                        and
                        {PropertyUtil.SafeGet(
                            () => MathUtil.numberToEnglish(parseInt(this.props.contractData?.leasehold?.optionTerm + '', 10), ' '),
                            'OPTION TERM WORD'
                        )}{' '}
                        ({PropertyUtil.SafeGet(() => this.props.contractData?.leasehold?.optionTerm, 'OPTION TERM')}){' '}
                        {this.props.contractData?.leasehold?.optionTermUnits}
                    </Text>
                );
            }

            primeTerm += optionTerm;
        }

        return (
            <Text style={[styles.bold, { fontSize: '9pt' }]}>
                {PropertyUtil.SafeGet(
                    () => MathUtil.numberToEnglish(parseInt(primeTerm, 10), ' '),
                    'PRIMARY TERM WORD'
                )}{' '}
                ({primeTerm}){' '}
                {this.props.contractData?.leasehold?.primaryTermUnits}
            </Text>
        );
    };

    render() {
        const leaseValues = PropertyUtil.SafeGet(() => this.props.contractData);
        const totalGrossAcres = leaseValues?.tracts?.reduce((acc: number, tract: AnyObject) => {
            if (tract.includeTract === true) {
                let tempGrossAcres = parseFloat(tract.grossAcres) || 0;
                return acc + tempGrossAcres;
            } else {
                return acc;
            }
        }, 0);

        return (
            <Document>
                <Page size="LEGAL" style={styles.page}>
                    <View>
                        <Text style={[{textAlign: 'left', fontSize: '11pt'}, styles.fontNormal, styles.bold]}>
                            <Text>STATE OF NORTH DAKOTA{'\n\n'}</Text>
                            <Text>COUNTY OF {!!this.props.location && this.props?.location?.counties[0].toUpperCase()} </Text>
                        </Text>
                    </View>

                    <View>
                        <Text style={[{ fontSize: '11pt', textAlign: 'center', fontFamily: 'Times-Bold' }, styles.mt2]}>
                            OIL, GAS AND MINERAL LEASE (PAID-UP)
                        </Text>
                    </View>

                    <View >
                        <Text style={[styles.fontNormal, styles.mt2, styles.text]} wrap={true}>
                            <Text>THIS AGREEMENT made and entered into this </Text>
                            <Text style={[styles.bold]}>
                                {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('Do'), 'DAY')}
                            </Text>{' '}day of{' '}
                            <Text style={[styles.bold]}>
                                {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('MMMM'), 'MONTH')},{' '}
                                {PropertyUtil.SafeGet(() => moment(leaseValues.effectiveDate).format('YYYY'), 'YEAR')}
                            </Text>
                            , by and between{' '}
                            <Text style={[styles.bold]}>
                                {PropertyUtil.SafeGet(() => leaseValues.ownersOnDocument, 'LESSOR NAME')}
                            </Text>
                            , whose address is {PropertyUtil.SafeGet(() => leaseValues.ownersAddressOnDocument, 'LESSOR ADDRESS')}, Party of the first
                            part, hereinafter called "Lessor" (whether one or more), and{' '}
                            <Text style={[styles.bold]}>{PropertyUtil.SafeGet(() => leaseValues.lesseeName, 'LESSEE NAME')}</Text>,
                            whose address is{' '}
                            {PropertyUtil.SafeGet(
                                () =>
                                    leaseValues.lesseeAddressLine1 +
                                    ', ' +
                                    leaseValues.lesseeCity +
                                    ', ' +
                                    leaseValues.lesseeState +
                                    ', ' +
                                    leaseValues.lesseeZipCode,
                                'LESSEE ADDRESS'
                            )}
                            , hereinafter called "Lessee".
                        </Text>
                    </View>

                    <View>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                            WITNESSETH: Lessor, in consideration of Ten and no/100's Dollars ($10.00) and other good and valuable 
                            consideration in hand paid, receipt of which is hereby acknowledged, and of the royalties herein provided and the 
                            agreements of Lessee herein contains, Lessor does hereby grant, lease and let exclusively unto Lessee its 
                            successors and assigns, all of the land hereinafter described, together with any reversionary rights therein for the 
                            purpose of exploring by geological, geophysical and all other methods, and of drilling, producing and operating
                            wells or mines for the recovery of oil, gas and other hydrocarbons, and all other minerals or substances, whether 
                            similar or dissimilar, that may be produced from any well or mine on the leased premises, including primary, 
                            secondary, tertiary, cycling, pressure maintenance methods of recovery, and all other methods, whether now 
                            known or unknown, with all incidental rights thereto, and to establish and utilize facilities for surface and 
                            subsurface disposal of salt water, and to construct, maintain and remove roadways, tanks, pipelines, electric power 
                            and telephone lines, power stations, machinery and structures thereon, to produce, store, transport, treat and 
                            remove all substances described above, and the products therefrom, together with the right of ingress and egress 
                            to and from said land across any other land now or hereafter owned by Lessor. The land hereby leased is situated 
                            in the County of Burke, State of North Dakota, and is described as follows:
                        </Text>

                        <Text style={[styles.mt1, styles.text, styles.bold, { fontSize: '11pt' }]} wrap={true}>
                            {leaseValues?.legalDescription}
                        </Text>

                        <Text style={[styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                            Containing{' '}{(PropertyUtil.SafeGet(() => totalGrossAcres, 'GROSS ACRES') as number)?.toFixed(2)}{' '}
                            gross acres, more or less 
                        </Text>
                            
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                            It is the intent of Lessee to lease all interest owned by Lessor in {this.displayTownshipInfo()} 
                            {' '} whether properly and completely described herein or not, and such land is hereinafter referred to as the “leased 
                            premises”.  In the event it is determined that Lessor actually owns more net mineral acres than assumed by the 
                            parties in the calculation of lease bonus and paid by Lessee, Lessor and Lessee agree that Lessee shall pay Lessor 
                            for such additional net acreage at the same bonus price per acre agreed upon for the execution of this 
                            lease.  Likewise, in the event it is determined that Lessor owns fewer net acres, or it is determined that Lessor’s
                            acreage is currently leased under a prior oil and gas lease, then Lessor agrees to reimburse Lessee for the bonus 
                            per acre paid for the acreage not owned by Lessor or under any prior oil and gas lease.
                        </Text>

                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text]} wrap={true}>
                            TO HAVE AND TO HOLD the leased premises for a term of {this.termValDisplay('normal')} from the date hereof, hereinafter 
                            called "primary term", and as long thereafter as oil, gas or other hydrocarbons, or other minerals or leased 
                            substances, or either or any of them, are produced from the leased premises or from lands with which the leased 
                            premises are pooled or unitized. 
                        </Text>

                        <Text style={[styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            In consideration of the premises it is hereby agreed as follows:
                        </Text>
                    </View>

                    <View>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            1. <Text style={[styles.underlined, styles.italic]}>Royalty On Oil.</Text> Lessee shall deliver to Lessor, at the well or to the credit of 
                            Lessor in the pipeline to which 
                            the well may be connected {this.displayRoyaltyFraction()}, of all oil and other liquid hydrocarbons produced and saved 
                            from the leased premises, or Lessee, at its option, may buy or sell such {this.royaltyFractionWords} and pay 
                            Lessor the market price for oil or liquid hydrocarbons of like grade and gravity prevailing in the field on the day 
                            such oil is run into pipelines or into storage tanks. Lessor's royalty interest in either case shall bear its proportion 
                            of any expenses for transporting and treating oil to make it marketable as crude.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            2. <Text style={[styles.underlined, styles.italic]}>Royalty On Gas.</Text> Lessee shall pay to Lessor as royalty on gas, including casinghead 
                            gas or other gaseous substances produced from said land and sold on or off of the premises, 
                            {'  '}{this.royaltyFractionWords}, of the net proceeds at the well received from the sale thereof, provided that on gas used off 
                            the leased premises or by Lessee in the manufacture of gasoline or other products therefrom, the royalty shall be the market 
                            value at the well of {this.royaltyFractionWords}, of the gas so used; as to all gas sold by Lessee under a written contract, 
                            the price received by Lessee for such gas shall be conclusively presumed to be the net proceeds at the well or the market value 
                            at the well for the gas sold after giving effect to applicable regulatory orders and after application of any applicable price 
                            adjustments specific in such contract or regulatory orders.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            3. <Text style={[styles.underlined, styles.italic]}>Royalty On Other Substances.</Text> 
                            {'  '}Lessee shall pay to Lessor, as royalty on any substances covered by this lease 
                            other than oil and gas and the products thereof which Lessee may elect to produce, save and market from the 
                            leased premises, {this.royaltyFractionWords}, of the proceeds received by Lessee from the sale thereof after deducting 
                            the processing costs.  
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            4. <Text style={[styles.underlined, styles.italic]}>Shut- In Gas Royalty.</Text> 
                            {'  '}If at any time, or from time to time, either before or after the expiration of the primary 
                            term of this lease, there is any gas well on the leased premises or on lands with which the leased premises are 
                            pooled or unitized and which is capable of producing in paying quantities, but which is shut in before or after 
                            production therefrom, such well shall be considered under all provisions of this lease as a well producing gas in 
                            paying quantities and this lease shall remain in force in like manner as though gas therefrom was actually being 
                            sold or used.  In such event, Lessee covenants and agrees to pay Lessor, as royalty, the sum of One Dollar ($ 1.00) 
                            per net acre per annum for the period commencing on the date such well is actually shut in, unless this lease is 
                            being maintained in force and effect by some other provision hereof, in which event such period shall commence 
                            on the date this lease ceases to be maintained in full force and effect by some other provision hereof. Payment or 
                            tender shall be made to Lessor at address above until written notice of address change has been received by Lessee 
                            from Lessor.  The first payment or tender shall be due and payable on or before one (1) year from the date such 
                            well is shut in, or one (1) year from the date this lease ceases to be maintained in force by some other provision 
                            hereof, and, if such payment or tender is made, this lease shall continue in effect for a further period of one (1)
                            year.  Unless gas from such well is produced and sold or used prior thereto, except temporary sales, or use for 
                            lease operations, subsequent payments or tenders shall be due annually thereafter on the anniversary date of the 
                            period for which such prior payment or tender was made. No additional payments shall be required if there is 
                            more than one shut-in gas well on the leased premises or on lands with which the leased premises are pooled or 
                            unitized.  The term “gas well" shall include wells capable of producing natural gas, condensate, or any gaseous 
                            substance, and wells classified as gas wells by any governmental authority having jurisdiction.  Notwithstanding 
                            any other provision to the contrary, this lease shall not terminate because of a failure to properly or timely make 
                            shut-in gas well payments or tenders unless Lessor shall have given Lessee written notice of such failure to 
                            properly or timely make such shut-in gas well payment and Lessee shall have failed for a period of forty-five 
                            days from receipt of such notice to tender such late payment or such payment in the proper amount, together 
                            with a late or improper payment penalty of $100.00.  
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            5. <Text style={[styles.underlined, styles.italic]}>Delay Rental.</Text>{' '}The lease is a “paid-up” lease and requires no rentals.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            6. <Text style={[styles.underlined, styles.italic]}>Drilling Operations.</Text> 
                            {'  '}If Lessee should drill and abandon as a dry hole a well on the leased premises, or if 
                            after the discovery of oil, gas or other minerals, the production thereof should cease from any cause, and, in either 
                            event there are no other producing wells on the leased premises or on lands with which they are pooled or unitized, 
                            or drilling or reworking operations are not being	conducted thereon, this lease shall not terminate if Lessee 
                            commences reworking or additional drilling operations on the leased premises within ninety (90) days thereafter 
                            or, if it be within the primary term, Lessee commences operations for drilling or reworking on or before the 
                            expiration of ninety (90) days from the date of such abandonment or cessation of production or the end of the 
                            primary term which is the later.  If, at the expiration of the primary term, oil, gas or other minerals are not being 
                            produced from the leased premises or from lands with which the leased premises are pooled or unitized, but Lessee 
                            is then engaged in operations for drilling or reworking of any well, this lease shall remain in force so long as such 
                            drilling or reworking operations are prosecuted, or reworking operations on any well or additional drilling 
                            operations are conducted on the leased premises, or on lands pooled or unitized therewith, with no cessation of 
                            more than ninety (90) consecutive days, and if any such operations result in production then as long thereafter as 
                            such production continues.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            7. <Text style={[styles.underlined, styles.italic]}>Pooling.</Text> 
                            {'  '}Lessee, at its option, is hereby given the right and power at any time and from time to time as a 
                            recurring right, either before or after production, as to all or any part of the leased premises and as to any one or 
                            more of the formations hereunder, to pool or unitize the leasehold estate and the mineral estate covered by this 
                            lease with other land, lease or leases in the immediate vicinity for the production of oil and gas, or separately for 
                            the production of either, when in Lessee's judgment it is necessary or advisable to do so, and irrespective of 
                            whether authority similar to this exists with respect to such other land, lease or leases. Likewise, units previously 
                            formed to include formations not producing oil or gas may be reformed to exclude such non-producing formations. 
                            The forming or reforming of any unit shall be accomplished by Lessee executing and filing of record a declaration 
                            of such unitization or reformation, which declaration shall describe the unit. Any unit may include land upon 
                            which a well has theretofore been completed or upon which operations for drilling have theretofore been 
                            commenced. Production, drilling or reworking operations or a well shut in for want of a market anywhere on a 
                            unit which includes all or a part of this lease shall be treated as if it were production, drilling or reworking 
                            operations or a well shut in for want of a market under this lease. In lieu of the royalties elsewhere herein specified, 
                            including shut-in gas royalties, Lessor shall receive on production from the unit so pooled royalties only on the 
                            portion of such production allocated to this lease; such allocation shall be that proportion of the unit production 
                            that the total number of surface acres covered by this lease and included in the unit bears to the total number of 
                            surface acres in such unit. In addition to the foregoing, Lessee shall have the right to unitize, pool, or combine all
                            or any part of the leased premises as to one or more of the formations thereunder with other lands in the same 
                            general area by entering into a cooperative or unit plan of development or operation approved by any governmental 
                            authority and, from time to time, with like approval, to modify, change or terminate any such plan or agreement 
                            and, in such event, the terms, conditions, and provisions of this lease shall be deemed modified to conform to the 
                            terms, conditions, and provisions of such approved cooperative or unit plan of development or operation and, 
                            particularly, all drilling and development requirements of this lease, express or implied, shall be satisfied by 
                            compliance with the drilling and development requirements of such plan or agreement, and this lease shall not 
                            terminate or expire during the life of such plan or agreement. In the event that said above described lands or any
                            part thereof, shall hereafter be operated under any such cooperative or unit plan of development or operation 
                            whereby the production therefrom is allocated to different portions of the land covered by said plan, then the 
                            production allocated to any particular tract of land shall, for the purpose of computing the royalties to be paid 
                            hereunder to Lessor, be regarded as having been produced from the particular tract of land to which it is allocated 
                            and not to any other tract of land; and the royalty payments to be made hereunder to Lessor shall be based upon 
                            production only as so allocated. Lessor shall formally express Lessor's consent to any cooperative or unit plan of 
                            development or operation adopted by Lessee and approved by any governmental agency by executing the same 
                            upon request of Lessee.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            8. <Text style={[styles.underlined, styles.italic]}>Use Of Oil, Gas And Water For Operations.</Text> 
                            {'  '}Lessee shall have the free use of oil, gas and water from the 
                            leased premises, except water from Lessor's wells and tanks, for all operations thereon and hereunder and the 
                            royalty on oil and gas shall be computed after deducting the amount so used whether on or off the leased premises. 
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            9. <Text style={[styles.underlined, styles.italic]}>Removal Of Equipment.</Text> 
                            {'  '}Lessee shall have the right, at any time during or after the expiration of this lease, to 
                            remove all property and fixtures placed on the leased premises by Lessee, including the right to withdraw and 
                            remove all casing. 
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            10. <Text style={[styles.underlined, styles.italic]}>Assignment Or Change of Ownership.</Text> 
                            {'  '}The rights of either party hereunder may be assigned in whole or in 
                            part and the provisions hereof shall extend to the heirs, executors, administrators, successors, and assigns, but no 
                            change or, division in ownership of the land, rentals or royalties, however accomplished, shall operate to enlarge 
                            the obligations or diminish the rights of Lessee.  No change or division in ownership of the land, rentals or 
                            royalties, however accomplished, shall be binding upon Lessee for any purpose and shall not impair the 
                            effectiveness of any payment theretofore made by Lessee (irrespective of whether Lessee has either actual or 
                            constructive knowledge hereof) until (60) days after such person acquiring any interest has furnished Lessee with 
                            the instrument or instruments, or certified copies thereof, constituting his chain of title from the original Lessor.  
                            An assignment of this lease, in whole or in part, shall, to the extent of such assignment, relieve and discharge 
                            Lessee of all obligations hereunder.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            11. <Text style={[styles.underlined, styles.italic]}>Force Majeure.</Text> 
                            {'  '}Lessee shall not be liable for any delays in its performance of any covenant or condition 
                            hereunder, express or implied, or for total or partial nonperformance thereof, due to force majeure.  The term 
                            “force majeure”, as used herein, shall mean any circumstance or any condition beyond the control of Lessee, 
                            including but not limited to acts of God and actions of the elements; acts of the public enemy; strikes; lockouts; 
                            accidents; epidemics, pandemics, laws, acts, rules, executive orders, regulations and orders of federal, state or 
                            municipal governments, or officers or agents thereof; failure of transportation; or the exhaustion, unavailability, 
                            or delays in delivery, of any product, labor, service or material.  If Lessee is required to cease drilling or reworking 
                            or producing operations on the leased premises by force majeure, then until such time as such force majeure is 
                            terminated and for a period of ninety (90) days after such termination, each and every provision of this lease that 
                            might operate to terminate it shall be suspended and this lease shall continue in full force and effect during such 
                            suspension period.  If any period of suspension occurs during the primary term, the time thereof shall be added to 
                            such term. 
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            12. <Text style={[styles.underlined, styles.italic]}>Lesser Interest Clause.</Text> 
                            {'  '}If Lessor does not own, or have the right to lease, the entire mineral interest in the 
                            land described above, then the royalties, and any other sums payable hereunder, shall be reduced and payable only 
                            in the proportion that the interest covered by this lease bears to the entire mineral interest in the above described 
                            land.  If the mineral interest covered hereby is subject to an outstanding nonparticipating royalty, such royalty 
                            shall be deducted from the royalties payable to Lessor hereunder.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            13. <Text style={[styles.underlined, styles.italic]}>Warranty.</Text> 
                            {'  '}Lessor hereby warrants and agrees to defend the title to the lands herein described and agrees 
                            that the Lessee shall have the right, any time, to redeem for Lessor, by payment, any mortgage, taxes or other liens 
                            on the leased premises in the event of default of payment by Lessor, and be subrogated to the rights of the holder 
                            thereof, and such payments may be deducted from any rental or royalties that may be payable to Lessor hereunder.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            14. <Text style={[styles.underlined, styles.italic]}>Third Party Offer.</Text> 
                            {'  '}If, during any time this lease is in force and effect, Lessor receives any bona fide offer, 
                            which Lessor is willing to accept from any party offering consideration to Lessor for a lease (top lease) covering 
                            any or all of the substances covered by this lease or covering all or a portion of the leased premises, with the top 
                            lease becoming effective upon the expiration of this lease, Lessor hereby agrees to immediately notify Lessee in 
                            writing of said offer, setting forth the proposed Lessee’s name, bonus consideration and royalty to be paid for such 
                            lease, and Lessor shall include a copy of the lease form to be utilized which form shall reflect all pertinent and 
                            relevant terms and conditions of the top lease.   Lessee shall have fifteen (15) days after receipt from Lessor, of a 
                            complete copy of any such offer to advise Lessor in writing of its election to enter into an oil and gas lease or 
                            extend the primary term of this lease with Lessor on equivalent terms and conditions as made in the top lease.  If 
                            Lessee fails to notify Lessor within the aforesaid fifteen (15) day period of its election to meet any such bona fide 
                            offer, Lessor shall have the right to accept said offer.  It is understood that any top lease acquired by Lessee herein 
                            pursuant to this provision, shall not limit the term or otherwise affect the validity of this lease.   Any top lease 
                            granted by Lessor to a third party without, prior notification to Lessee, as set out above, shall be null and void.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            15. <Text style={[styles.underlined, styles.italic]}>Surrender.</Text> 
                            {'  '}Lessee, its successors and assigns, shall have the right at any time, to surrender this lease, in 
                            whole or in part, by delivering or mailing a release of record in the county, or counties, in which the leased 
                            premises are situated, and thereupon, Lessee shall be relieved from all obligations, expressed or implied, of this 
                            lease as to the acreage so surrendered.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            16. <Text style={[styles.underlined, styles.italic]}>Breach or Default.</Text> 
                            {'  '}No litigation shall be initiated by Lessor for damages, forfeiture or cancellation with 
                            respect to any material breach or default by Lessee hereinunder unless Lessor has given Lessee written notice of 
                            the breach or default prior to litigation being initiated in regard to the same. Lessee shall have a period of forty-
                            five (45) days from receipt of such notice to cure such breach or default prior to litigation being initiated in regard 
                            to the same.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            17. <Text style={[styles.underlined, styles.italic]}>Parties Bound.</Text> 
                            {'  '}This lease and all of the rights, obligations and conditions hereof shall be binding upon 
                            each party executing this instrument and heirs, devises, successors and assigns.  This lease may be executed in 
                            any number of counterparts, each of which is deemed an original, and all of which taken together shall constitute 
                            one and the same lease.  Should any party named above as Lessor fail to execute this lease, or should any party 
                            execute this lease who is not named above as a Lessor, it shall nevertheless be binding upon the party or parties 
                            executing the same.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            18. <Text style={[styles.underlined, styles.italic]}>Headings For Convenience.</Text> 
                            {'  '}The paragraph headings herein are for convenience only and shall not be 
                            considered or construed to limit the subject matter of any paragraph.
                        </Text>
                        <Text style={[{textAlign: 'justify'}, styles.fontNormal, styles.mt1, styles.text, styles.indent]} wrap={true}>
                            19. <Text style={[styles.underlined, styles.italic]}>Memorandum.</Text> 
                            {'  '}Upon Lessee’s request, Lessor shall promptly execute a Memorandum of Oil, Gas and 
                            Mineral Lease (Paid Up) (the “Memorandum”) for the purpose of recording so Lessee can provide notice of the 
                            lease in the real property records of {this.props?.location?.counties[0]} County, North Dakota. Lessor agrees that this lease shall not be 
                            recorded in the real property records of any county unless Lessee so chooses in lieu of the Memorandum.  
                        </Text>
                    </View>

                    <View>
                        <Text style={[styles.fontNormal, styles.mt1, styles.indent2]}>
                            IN WITNESS WHEREOF, this instrument is executed the day and year first above written.
                        </Text>
                    </View>

                    <View style={{ marginTop: '40px'}}>
                        <Text style={[styles.fontNormal]}>
                        _____________________________________
                        </Text>
                        <Text style={[styles.fontNormal]}>
                            {this.props?.contractData?.ownersOnDocument}
                        </Text>
                    </View>

                    <View style={{ marginTop: '80px'}}>
                        <Text style={[styles.fontNormal]}>
                            <Text>THE STATE OF ________________{' )\n'}</Text> 
                            <Text style={styles.indent5}>{'                               )\n'}</Text> 
                            <Text>COUNTY OF __________________{' )'}</Text>
                        </Text>
                    </View>

                    <View style={styles.mt1}>
                        <Text style={[styles.fontNormal, styles.indent]}>
                            Before me, the undersigned authority, on this day personally appeared 	_____________________________ 
                            known to me to be the identical person whose name is subscribed to the foregoing instrument, and acknowledged 
                            to me that they executed the same for the purposes and consideration therein expressed.
                        </Text>
                    </View>

                    <View style={styles.mt1}>
                        <Text style={[styles.fontNormal]}>
                            Given under my hand and seal of office this the_____________day of_______________, A.D. 20____.
                        </Text>
                    </View>

                    <View style={styles.mt2}>
                        <Text style={[{marginLeft: '45%'}, styles.fontNormal]}>_______________________________________ </Text>
                        <Text style={[{marginLeft: '50%'}, styles.fontNormal]}>Notary Public, State of______________ </Text>
                    </View>

                    <Text
                        style={[{marginBottom: '20'}, styles.pageNumbers]}
                        render={({ pageNumber }) => {
                            return `${pageNumber}`;
                        }}
                        fixed={true}
                    />
                </Page>
            </Document>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/OGTemplates/FormonteraTemplate.tsx